/* eslint-disable no-undef */
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
  makeStyles
} from '@material-ui/core';
import { LoaderBar } from 'src/components';
import { HoldModal } from 'src/components/modal/HoldModal';
import { colors } from 'src/constants';
import { usePermissions, useSnackBar } from 'src/hooks';
import { useTaptapCustomer } from 'src/hooks/taptap/use-tap-tap-customer';
import { useTaptapFlow } from 'src/hooks/taptap/use-tap-tap-flow';
import useUserInfo from 'src/hooks/user/use-user-info';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { SupabaseClientService } from 'src/services';
import {
  TAPTAP_OPERATION_DASHBOARD_OTHERS_VALUE,
  taptapBranchReleased,
  taptapFetchCustomerTimeline,
  findSelectedQuickFilterViaParamV2,
  getCustomersQueryBuilder,
  getTaptapOperationDashboardAccessibleColumns,
  isInsertNewCustomerDataIsValid,
  taptapGetCustomerDetailsService,
  taptapUpdateCustomerViaId
} from 'src/redux/slices/taptap-customer';
import { taptapCustomerField } from 'src/redux/slices/taptap-customer/constants/taptap-fields';
import {
  taptapOptionsStatusesV2,
  taptapStatuses
} from 'src/redux/slices/taptap-customer/constants/taptap-flow-process';
import { taptapServiceType } from 'src/redux/slices/taptap-customer/constants/taptap-service-type';
import { taptapSalesTransferBranch } from 'src/redux/slices/taptap-customer/constants/taptap-form-fields';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';

import {
  TapTapCustomerData,
  GetTapTapCustomersPayload,
  TapTapOperationDashboardColumn,
  TapTapCustomerPayment,
  TaptapTimelineStatuses
} from 'src/redux/slices/taptap-customer/types';
import {
  dateToday,
  formatCurrency,
  formatDate,
  getBranchColor
} from 'src/utils';
import TapTapRunningDuration from '../components/TapTapRunningDuration';
import { TicketDialog } from '../components/TicketDialog';
import CommentIcon from '@material-ui/icons/Comment';
import { cloneDeep } from 'lodash';
import { isSalesTransferDestinationBranchMatch } from './sales-transfer-branch-match';

const {
  actions: taptapCustomerActions,
  selectors: taptapCustomerSelectors
} = slices.taptapCustomer;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: 'calc(100vh - 220px)',
    maxHeight: 'calc(100vh - 220px)',
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'hidden'
  },
  board: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 450,
    gap: theme.spacing(2),
    alignItems: 'start',
    height: '100%',
    minHeight: 0
  },
  column: {
    backgroundColor: colors.grey[100],
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    height: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  focusedColumn: {
    // borderColor: colors.lightBlue[700],
    // boxShadow: `0 0 0 2px ${colors.lightBlue[100]}`,
    transition: 'border-color 200ms ease, box-shadow 200ms ease'
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    flex: 1,
    minHeight: 0,
    maxHeight: '100%',
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5)
  },
  card: {
    borderRadius: 8,
    cursor: 'pointer',
    position: 'relative',
    width: '100%',
    alignSelf: 'flex-start',
    height: 'fit-content',
    overflow: 'visible',
    '&:hover': {
      boxShadow: theme.shadows[6]
    }
  },
  commentEmblem: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: colors.red[500],
    color: colors.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${colors.common.white}`,
    boxShadow: theme.shadows[2]
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  },
  dialogPaper: {
    borderRadius: 8
  },
  dialogContent: {
    backgroundColor: colors.grey[100]
  },
  previewCard: {
    backgroundColor: colors.common.white,
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1)
  },
  branchText: {
    fontWeight: 700
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: colors.grey[600]
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1.5)
  },
  timelineGrid: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1.2fr 0.8fr',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid #f5f5f5'
  }
}));

interface Props {
  customers: TapTapCustomerData[];
  isCustomersLoading: boolean;
  isNextPageLoading: boolean;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  onEditPayment: (payment: TapTapCustomerPayment) => void;
  isReadOnly?: boolean;
  includeAllTicketStatuses?: boolean;
  selectedDashboardView?: string | null;
  statusFilter?: string;
}

const getColumnCustomers = (
  customers: TapTapCustomerData[],
  column: TapTapOperationDashboardColumn,
  knownDashboardStatuses: Set<string>,
  selectedBranchValues: string[]
) => {
  if (column.value === TAPTAP_OPERATION_DASHBOARD_OTHERS_VALUE) {
    return customers.filter(
      (customer) =>
        !customer.status || !knownDashboardStatuses.has(customer.status)
    );
  }

  if (column.key === 'sales_transfer_on_going') {
    return customers.filter((customer) =>
      isCustomerVisibleInOperationDashboardColumn(
        customer,
        column,
        selectedBranchValues
      )
    );
  }

  if (column.key === 'sales_transfer_complete') {
    return customers.filter((customer) =>
      isCustomerVisibleInOperationDashboardColumn(
        customer,
        column,
        selectedBranchValues
      )
    );
  }

  return customers.filter(
    (customer) =>
      customer.status === column.value &&
      isCustomerVisibleInOperationDashboardColumn(
        customer,
        column,
        selectedBranchValues
      )
  );
};

const getStatusBgColor = (status?: string) =>
  taptapOptionsStatusesV2.find((option) => option.value === status)?.bgColor ||
  colors.grey[400];

const defaultHiddenDashboardStatuses = new Set([
  taptapStatuses.COMPLETED,
  taptapStatuses.CANCELLED,
  TAPTAP_OPERATION_DASHBOARD_OTHERS_VALUE
]);

const OPERATION_DASHBOARD_PAGE_SIZE = 10;

const salesTransferOnGoingStatuses = [
  taptapStatuses.FOR_PAYMENT,
  taptapStatuses.DONE_PAYMENT,
  taptapStatuses.PREPARING_ITEMS,
  taptapStatuses.RELEASING,
  taptapStatuses.IN_HOUSE_DELIVERY_FOR_SALES_TRANSFER
];

const salesTransferServiceTypes = [
  taptapServiceType.BUILD_SALES_TRANSFER,
  taptapServiceType.PARTS_OUT_SALES_TRANSFER
];

type OperationDashboardColumnState = {
  customers: TapTapCustomerData[];
  count: number;
  loading: boolean;
  hasMore: boolean;
};

/**
 * utils function para maconvert into branch keyword for sales transfer requestee
 * @param branch
 * @returns
 */
const getSalesTransferBranchKeyword = (branch?: string) => {
  switch (branch) {
    case taptapBranchReleased.EARNSHAW:
      return taptapSalesTransferBranch.SAMPALOC;
    case taptapBranchReleased.LPC:
      return taptapSalesTransferBranch.LPC;
    case taptapBranchReleased.EXPERIENCE:
      return 'QC';
    default:
      return '';
  }
};

const getSelectedBranchValues = (payload: GetTapTapCustomersPayload) => {
  const branchValue = payload.filters?.conditions?.find(
    (condition) => condition.field === taptapCustomerField.BRANCH_RELEASED
  )?.value;

  if (Array.isArray(branchValue)) {
    return branchValue;
  }

  return branchValue ? [branchValue] : [];
};

const getSalesTransferDestinationBranchKeyword = (
  kindOfSalesTransfer?: string
) => {
  const [, destinationBranch = ''] = (kindOfSalesTransfer || '').split(' TO ');

  return destinationBranch.trim();
};

const isSelectedSalesTransferBranch = (
  selectedBranchValues: string[],
  salesTransferBranchKeyword?: string
) => {
  if (selectedBranchValues.length <= 0) {
    return true;
  }

  return selectedBranchValues
    .map(getSalesTransferBranchKeyword)
    .filter(Boolean)
    .some((branchKeyword) =>
      isSalesTransferDestinationBranchMatch(
        branchKeyword,
        salesTransferBranchKeyword
      )
    );
};

const isSalesTransferRequesteeTicket = (
  customer: TapTapCustomerData,
  selectedBranchValues: string[]
) => {
  if (
    customer.status !== taptapStatuses.PENDING_SALES_TRANSFER ||
    selectedBranchValues.length <= 0
  ) {
    return false;
  }

  if (selectedBranchValues.includes(customer.branch_released || '')) {
    return false;
  }

  return selectedBranchValues
    .map(getSalesTransferBranchKeyword)
    .filter(Boolean)
    .some((branchKeyword) =>
      customer.kind_of_sales_transfer
        ?.toLowerCase()
        ?.includes(branchKeyword.toLowerCase())
    );
};

const isSalesTransferOnGoingTicket = (
  customer: TapTapCustomerData,
  selectedBranchValues: string[]
) => {
  return (
    salesTransferServiceTypes.includes(customer.service_type || '') &&
    salesTransferOnGoingStatuses.includes(customer.status || '') &&
    isSalesTransferRequesteeTicket(
      {
        ...customer,
        status: taptapStatuses.PENDING_SALES_TRANSFER
      },
      selectedBranchValues
    )
  );
};

const isSalesTransferCompleteTicket = (
  customer: TapTapCustomerData,
  selectedBranchValues: string[]
) => {
  return (
    salesTransferServiceTypes.includes(customer.service_type || '') &&
    customer.status === taptapStatuses.COMPLETED &&
    isSalesTransferDestinationTicket(customer, selectedBranchValues)
  );
};

const isSalesTransferDestinationTicket = (
  customer: TapTapCustomerData,
  selectedBranchValues: string[]
) => {
  return isSelectedSalesTransferBranch(
    selectedBranchValues,
    getSalesTransferDestinationBranchKeyword(customer.kind_of_sales_transfer)
  );
};

const isCustomerVisibleInOperationDashboardColumn = (
  customer: TapTapCustomerData,
  column: TapTapOperationDashboardColumn,
  selectedBranchValues: string[]
) => {
  if (column.key === 'sales_transfer_request') {
    return isSalesTransferRequesteeTicket(customer, selectedBranchValues);
  }

  if (column.key === 'pending_sales_transfer') {
    return !isSalesTransferRequesteeTicket(customer, selectedBranchValues);
  }

  if (column.key === 'sales_transfer_on_going') {
    return isSalesTransferOnGoingTicket(customer, selectedBranchValues);
  }

  if (column.key === 'sales_transfer_complete') {
    return isSalesTransferCompleteTicket(customer, selectedBranchValues);
  }

  if (
    column.key === 'sales_transfer_receiving' ||
    column.key === 'sales_transfer_releasing'
  ) {
    return isSalesTransferDestinationTicket(customer, selectedBranchValues);
  }

  if (column.key === 'in_house_delivery_for_sales_transfer') {
    return isSalesTransferDestinationTicket(customer, selectedBranchValues);
  }

  return true;
};

const isSalesTransferVirtualColumn = (column: TapTapOperationDashboardColumn) =>
  column.key === 'pending_sales_transfer' ||
  column.key === 'sales_transfer_request' ||
  column.key === 'sales_transfer_on_going' ||
  column.key === 'sales_transfer_complete';

const getStatusFilteredPayload = (
  payload: GetTapTapCustomersPayload,
  column: TapTapOperationDashboardColumn,
  knownDashboardStatuses: Set<string>,
  page: number
): GetTapTapCustomersPayload => {
  const nextPayload = cloneDeep(payload);
  const conditions = nextPayload.filters?.conditions || [];
  const conditionsWithoutStatus = conditions.filter(
    (condition) => condition.field !== taptapCustomerField.STATUS
  );
  const statusCondition =
    column.value === TAPTAP_OPERATION_DASHBOARD_OTHERS_VALUE
      ? {
          field: taptapCustomerField.STATUS,
          operator: 'not-in',
          value: Array.from(knownDashboardStatuses)
        }
      : column.key === 'sales_transfer_on_going'
      ? {
          field: taptapCustomerField.STATUS,
          operator: 'in',
          value: salesTransferOnGoingStatuses
        }
      : column.key === 'sales_transfer_complete'
      ? {
          field: taptapCustomerField.STATUS,
          operator: 'eq',
          value: taptapStatuses.COMPLETED
        }
      : {
          field: taptapCustomerField.STATUS,
          operator: 'eq',
          value: column.value
        };

  nextPayload.pagination = {
    page,
    pageSize: OPERATION_DASHBOARD_PAGE_SIZE
  };
  nextPayload.filters = {
    logic: 'and',
    conditions: [...conditionsWithoutStatus, statusCondition]
  };

  return nextPayload;
};

const getPayloadWithBranchFallback = (
  payload: GetTapTapCustomersPayload,
  branchValues: string[]
) => {
  const nextPayload = cloneDeep(payload);
  const conditions = nextPayload.filters?.conditions || [];
  const hasBranchCondition = conditions.some(
    (condition) => condition.field === taptapCustomerField.BRANCH_RELEASED
  );

  if (hasBranchCondition || branchValues.length <= 0) {
    return nextPayload;
  }

  nextPayload.filters = {
    ...nextPayload.filters,
    conditions: [
      ...conditions,
      {
        field: taptapCustomerField.BRANCH_RELEASED,
        operator: 'in',
        value: branchValues
      }
    ]
  };

  return nextPayload;
};

const Component: FC<Props> = ({
  customers,
  isCustomersLoading,
  isNextPageLoading,
  onEditPayment,
  isReadOnly = false,
  includeAllTicketStatuses = false,
  selectedDashboardView = null,
  statusFilter = ''
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();
  const { createTicketLog } = useTaptapCustomer();
  const { userPermissionsStringArray } = usePermissions();
  const { userDetails, userBranchNicknames } = useUserInfo();
  const operationDashboardColumns = useAppSelector(
    taptapCustomerSelectors.selectTaptapOperationDashboardColumns
  );
  const selectedFilter = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerFilter
  );
  const selectedGetPayload = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerPayload
  );

  const [selectedCustomerId, setSelectedCustomerId] = useState<number>();
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [timeline, setTimeline] = useState<TaptapTimelineStatuses[]>([]);
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const [holdReason, setHoldReason] = useState<string | undefined>();
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [newCommentCustomerIds, setNewCommentCustomerIds] = useState<
    Set<number>
  >(() => new Set());
  const [focusedColumnKey, setFocusedColumnKey] = useState<string | null>(null);
  const selectedCustomerIdRef = useRef<number | undefined>();
  const searchValueRef = useRef<string>('');
  const columnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const lastAutoFocusKeyRef = useRef<string>('');
  const isMountedRef = useRef(true);
  const columnFetchGenerationRef = useRef(0);
  const [pendingFocusStatus, setPendingFocusStatus] = useState<string | null>(
    null
  );
  const [columnCustomerState, setColumnCustomerState] = useState<
    Record<string, OperationDashboardColumnState>
  >({});

  const visibleDashboardColumns = useMemo(() => {
    const selectedQuickViewLabel =
      selectedDashboardView ||
      findSelectedQuickFilterViaParamV2(selectedFilter, selectedGetPayload.sort)
        ?.label;
    const accessibleColumns = getTaptapOperationDashboardAccessibleColumns(
      operationDashboardColumns,
      userPermissionsStringArray,
      selectedQuickViewLabel
    );

    return accessibleColumns
      .filter((column) => column.visible)
      .filter(
        (column) =>
          includeAllTicketStatuses ||
          !defaultHiddenDashboardStatuses.has(column.value)
      );
  }, [
    operationDashboardColumns,
    selectedFilter,
    selectedGetPayload.sort,
    selectedDashboardView,
    includeAllTicketStatuses,
    userPermissionsStringArray
  ]);

  const knownDashboardStatuses = useMemo(
    () =>
      new Set(
        operationDashboardColumns
          .filter(
            (column) => column.value !== TAPTAP_OPERATION_DASHBOARD_OTHERS_VALUE
          )
          .map((column) => column.value)
      ),
    [operationDashboardColumns]
  );

  const selectedBranchValues = useMemo(
    () => getSelectedBranchValues(selectedGetPayload),
    [selectedGetPayload]
  );
  const effectiveBranchValues = useMemo(
    () =>
      selectedBranchValues.length > 0
        ? selectedBranchValues
        : userBranchNicknames,
    [selectedBranchValues, userBranchNicknames]
  );

  const columnsWithCustomers = useMemo(
    () =>
      visibleDashboardColumns.map((column) => {
        const localState = columnCustomerState[column.key];

        return {
          ...column,
          customers:
            localState?.customers ||
            getColumnCustomers(
              customers,
              column,
              knownDashboardStatuses,
              effectiveBranchValues
            ),
          count: localState?.count,
          loading: localState?.loading || false,
          hasMore: localState?.hasMore || false
        };
      }),
    [
      columnCustomerState,
      customers,
      effectiveBranchValues,
      knownDashboardStatuses,
      visibleDashboardColumns
    ]
  );

  const selectedCustomer = useMemo(
    () =>
      columnsWithCustomers
        .flatMap((column) => column.customers)
        .find((customer) => customer.id === selectedCustomerId) ||
      customers.find((customer) => customer.id === selectedCustomerId),
    [columnsWithCustomers, customers, selectedCustomerId]
  );
  const selectedCustomerForFlow = useMemo(
    () => (selectedCustomer ? { ...selectedCustomer, timeline } : undefined),
    [selectedCustomer, timeline]
  );
  const paymentTotal = useMemo(
    () =>
      selectedCustomer?.customers_payments?.reduce(
        (sum, payment) => sum + (payment.amount || 0),
        0
      ) || 0,
    [selectedCustomer]
  );
  const [
    hasSelectedCustomerSystemGeneratedTicket,
    setHasSelectedCustomerSystemGeneratedTicket
  ] = useState(false);
  const { nextStatus, canProceed, nextAction } = useTaptapFlow(
    selectedCustomerForFlow,
    {
      hasSystemGeneratedTicket: hasSelectedCustomerSystemGeneratedTicket,
      isReadOnly,
      isStatusLoading: isStatusLoading || isHistoryLoading || isCommentLoading,
      paymentTotal
    }
  );

  const selectedFilterConditionCount = selectedFilter?.conditions?.length || 0;
  const dashboardAutoFocusKey = useMemo(
    () =>
      [
        selectedGetPayload.search || '',
        selectedFilterConditionCount,
        statusFilter,
        visibleDashboardColumns.map((column) => column.key).join('|')
      ].join('__'),
    [
      selectedFilterConditionCount,
      selectedGetPayload.search,
      statusFilter,
      visibleDashboardColumns
    ]
  );

  const focusStatusColumn = useCallback(
    (status?: string | null) => {
      if (!status) {
        return;
      }

      const targetColumn = columnsWithCustomers.find(
        (column) => column.value === status
      );
      const targetElement = targetColumn
        ? columnRefs.current[targetColumn.key]
        : null;

      setFocusedColumnKey(targetColumn?.key || null);
      targetElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    },
    [columnsWithCustomers]
  );

  useEffect(() => {
    selectedCustomerIdRef.current = selectedCustomerId;
    setHasSelectedCustomerSystemGeneratedTicket(false);
    setIsCommentLoading(false);
  }, [selectedCustomerId]);

  useEffect(() => {
    searchValueRef.current = selectedGetPayload.search || '';
  }, [selectedGetPayload.search]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const fetchCustomersByColumn = useCallback(
    async (
      column: TapTapOperationDashboardColumn,
      page = 1,
      append = false,
      fetchGeneration = columnFetchGenerationRef.current
    ) => {
      const isCurrentRequest = () =>
        isMountedRef.current &&
        fetchGeneration === columnFetchGenerationRef.current;

      setColumnCustomerState((prev) => ({
        ...prev,
        [column.key]: {
          customers: append ? prev[column.key]?.customers || [] : [],
          count: prev[column.key]?.count || 0,
          loading: true,
          hasMore: append ? prev[column.key]?.hasMore || false : false
        }
      }));

      const response = await getCustomersQueryBuilder({
        payloadArg: getStatusFilteredPayload(
          getPayloadWithBranchFallback(
            selectedGetPayload,
            effectiveBranchValues
          ),
          column,
          knownDashboardStatuses,
          page
        )
      });

      if (!isCurrentRequest()) {
        return;
      }

      if (response.error) {
        console.error('Failed to fetch TapTap operation dashboard column:', {
          column: column.value,
          error: response.error
        });
        setColumnCustomerState((prev) => ({
          ...prev,
          [column.key]: {
            customers: prev[column.key]?.customers || [],
            count: prev[column.key]?.count || 0,
            loading: false,
            hasMore: prev[column.key]?.hasMore || false
          }
        }));
        return;
      }

      const nextCustomers = (response.data || []).filter((customer) =>
        isCustomerVisibleInOperationDashboardColumn(
          customer,
          column,
          effectiveBranchValues
        )
      );

      if (!isCurrentRequest()) {
        return;
      }

      setColumnCustomerState((prev) => {
        const existingCustomers = append
          ? prev[column.key]?.customers || []
          : [];
        const mergedCustomers = append
          ? [...existingCustomers, ...nextCustomers]
          : nextCustomers;
        const count = isSalesTransferVirtualColumn(column)
          ? mergedCustomers.length
          : response.count ?? mergedCustomers.length;

        return {
          ...prev,
          [column.key]: {
            customers: mergedCustomers,
            count,
            loading: false,
            hasMore:
              !isSalesTransferVirtualColumn(column) &&
              mergedCustomers.length < count
          }
        };
      });
    },
    [effectiveBranchValues, knownDashboardStatuses, selectedGetPayload]
  );

  useEffect(() => {
    const fetchGeneration = columnFetchGenerationRef.current + 1;
    columnFetchGenerationRef.current = fetchGeneration;
    setColumnCustomerState({});
    visibleDashboardColumns.forEach((column) => {
      fetchCustomersByColumn(column, 1, false, fetchGeneration);
    });
  }, [fetchCustomersByColumn, visibleDashboardColumns]);

  const handleColumnScroll = useCallback(
    (
      event: React.UIEvent<HTMLElement>,
      column: TapTapOperationDashboardColumn
    ) => {
      const columnState = columnCustomerState[column.key];

      if (!columnState?.hasMore || columnState.loading) {
        return;
      }

      const element = event.currentTarget;
      const distanceFromBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight;

      if (distanceFromBottom <= 10) {
        const nextPage =
          Math.floor(
            columnState.customers.length / OPERATION_DASHBOARD_PAGE_SIZE
          ) + 1;

        fetchCustomersByColumn(column, nextPage, true);
      }
    },
    [columnCustomerState, fetchCustomersByColumn]
  );

  useEffect(() => {
    if (
      isCustomersLoading ||
      lastAutoFocusKeyRef.current === dashboardAutoFocusKey ||
      columnsWithCustomers.some((column) => column.loading)
    ) {
      return;
    }

    const hasAppliedSearch = Boolean(searchValueRef.current.trim());
    const hasAppliedFilters = Boolean(
      selectedFilterConditionCount || statusFilter
    );
    const hasActiveDashboardFilter = hasAppliedSearch || hasAppliedFilters;
    const columnToFocus = statusFilter
      ? columnsWithCustomers.find((column) => column.value === statusFilter)
      : hasActiveDashboardFilter
      ? columnsWithCustomers.find((column) => column.customers.length > 0)
      : columnsWithCustomers[0];

    if (!columnToFocus) {
      setFocusedColumnKey(null);
      return;
    }

    lastAutoFocusKeyRef.current = dashboardAutoFocusKey;
    setFocusedColumnKey(
      hasActiveDashboardFilter && columnToFocus.customers.length > 0
        ? columnToFocus.key
        : null
    );
    window.requestAnimationFrame(() => {
      columnRefs.current[columnToFocus.key]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: hasActiveDashboardFilter ? 'center' : 'start'
      });
    });
  }, [
    dashboardAutoFocusKey,
    columnsWithCustomers,
    isCustomersLoading,
    selectedFilterConditionCount,
    statusFilter
  ]);

  useEffect(() => {
    if (!pendingFocusStatus) {
      return;
    }

    focusStatusColumn(pendingFocusStatus);
    setPendingFocusStatus(null);
  }, [focusStatusColumn, pendingFocusStatus]);

  /**
   * Pang fetch ng realtime database and apply to state
   */
  const applyRealtimeCustomerToColumns = useCallback(
    (customer?: TapTapCustomerData | null) => {
      if (!customer?.id) {
        return;
      }

      setColumnCustomerState((prev) => {
        const nextState = { ...prev };

        visibleDashboardColumns.forEach((column) => {
          const currentColumnState = nextState[column.key];

          if (!currentColumnState) {
            return;
          }

          const currentCustomers = currentColumnState.customers || [];
          const existingIndex = currentCustomers.findIndex(
            (item) => item.id === customer.id
          );
          const existsInColumn = existingIndex !== -1;
          const shouldExistInColumn =
            !customer.is_deleted &&
            isCustomerVisibleInOperationDashboardColumn(
              customer,
              column,
              effectiveBranchValues
            ) &&
            isInsertNewCustomerDataIsValid(
              customer,
              getStatusFilteredPayload(
                selectedGetPayload,
                column,
                knownDashboardStatuses,
                1
              )
            );

          if (existsInColumn && !shouldExistInColumn) {
            nextState[column.key] = {
              ...currentColumnState,
              customers: currentCustomers.filter(
                (item) => item.id !== customer.id
              ),
              count: Math.max((currentColumnState.count || 0) - 1, 0),
              hasMore:
                currentColumnState.hasMore ||
                currentCustomers.length - 1 < currentColumnState.count
            };
            return;
          }

          if (existsInColumn && shouldExistInColumn) {
            const nextCustomers = [...currentCustomers];
            nextCustomers[existingIndex] = {
              ...customer,
              customers_payments: customer.customers_payments || []
            };

            nextState[column.key] = {
              ...currentColumnState,
              customers: nextCustomers
            };
            return;
          }

          if (!existsInColumn && shouldExistInColumn) {
            nextState[column.key] = {
              ...currentColumnState,
              customers: [
                {
                  ...customer,
                  customers_payments: customer.customers_payments || []
                },
                ...currentCustomers
              ],
              count: (currentColumnState.count || 0) + 1,
              hasMore: isSalesTransferVirtualColumn(column)
                ? false
                : currentColumnState.hasMore
            };
          }
        });

        return nextState;
      });
    },
    [
      effectiveBranchValues,
      knownDashboardStatuses,
      selectedGetPayload,
      visibleDashboardColumns
    ]
  );

  /**
   * Pang clean lang
   */
  const removeRealtimeCustomerFromColumns = useCallback(
    (customerId?: number) => {
      if (!customerId) {
        return;
      }

      setColumnCustomerState((prev) => {
        const nextState = { ...prev };

        Object.entries(nextState).forEach(([columnKey, currentColumnState]) => {
          const currentCustomers = currentColumnState.customers || [];
          const hasCustomer = currentCustomers.some(
            (customer) => customer.id === customerId
          );

          if (!hasCustomer) {
            return;
          }

          nextState[columnKey] = {
            ...currentColumnState,
            customers: currentCustomers.filter(
              (customer) => customer.id !== customerId
            ),
            count: Math.max((currentColumnState.count || 0) - 1, 0)
          };
        });

        return nextState;
      });
    },
    []
  );

  /**
   * Pang clean lang din
   */
  const refreshRealtimeCustomer = useCallback(
    async (customerId?: number) => {
      if (!customerId) {
        return;
      }

      const { data, error } = await taptapGetCustomerDetailsService(customerId);

      if (error) {
        console.error('Failed to fetch realtime TapTap customer:', {
          customerId,
          error
        });
        return;
      }

      applyRealtimeCustomerToColumns(data?.[0]);
    },
    [applyRealtimeCustomerToColumns]
  );

  /**
   * Pinaka fetch
   */
  useEffect(() => {
    const customerTableChanges = SupabaseClientService.channel(
      'operation_dashboard_customers_channel'
    )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customers'
        },
        (payload) => {
          if (payload?.eventType === 'DELETE') {
            removeRealtimeCustomerFromColumns(Number(payload?.old?.id));
            return;
          }

          refreshRealtimeCustomer(Number(payload?.new?.id));
        }
      )
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('Operation dashboard customers realtime failed:', {
            status,
            error
          });
        }
      });

    const customersPaymentsTableChanges = SupabaseClientService.channel(
      'operation_dashboard_customers_payments_channel'
    )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customers_payments'
        },
        (payload) => {
          const paymentPayload: any = payload;
          const customerId = Number(
            paymentPayload['new']?.customer_id ||
              paymentPayload.old?.customer_id
          );

          refreshRealtimeCustomer(customerId);
        }
      )
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error(
            'Operation dashboard customer payments realtime failed:',
            {
              status,
              error
            }
          );
        }
      });

    return () => {
      SupabaseClientService.removeChannel(customerTableChanges);
      SupabaseClientService.removeChannel(customersPaymentsTableChanges);
    };
  }, [refreshRealtimeCustomer, removeRealtimeCustomerFromColumns]);

  useEffect(() => {
    //eto yung pang realtime call sa supabase - detect changes sa server
    const ticketCommentsTableChanges = SupabaseClientService.channel(
      'operation_dashboard_ticket_comments_channel'
    )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_comments'
        },
        (payload) => {
          const customerId = Number(payload?.new?.customer_id);

          if (!customerId || selectedCustomerIdRef.current === customerId) {
            return;
          }

          setNewCommentCustomerIds((prev) => {
            const next = new Set(prev);
            next.add(customerId);
            return next;
          });
        }
      )
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('Ticket comments realtime channel failed:', {
            status,
            error
          });
        }
      });

    return () => {
      SupabaseClientService.removeChannel(ticketCommentsTableChanges);
    };
  }, []);

  const fetchPreviewHistory = useCallback(async (customerId?: number) => {
    if (!customerId) {
      setTimeline([]);
      return;
    }

    setIsHistoryLoading(true);

    const timelineResponse = await taptapFetchCustomerTimeline(customerId);

    if (!timelineResponse.error) {
      setTimeline(timelineResponse.data || []);
    } else {
      console.error('Failed to fetch ticket timeline:', timelineResponse.error);
    }

    setIsHistoryLoading(false);
  }, []);

  useEffect(() => {
    fetchPreviewHistory(selectedCustomerId);
  }, [fetchPreviewHistory, selectedCustomerId]);

  const closePreviewDialog = () => {
    setSelectedCustomerId(undefined);
    setTimeline([]);
    setHoldReason('');
    setPendingStatus(null);
    setIsHoldModalOpen(false);
  };

  const openPreviewDialog = useCallback(
    (customerId?: number) => {
      setSelectedCustomerId(customerId);

      if (!customerId) {
        return;
      }

      setNewCommentCustomerIds((prev) => {
        if (!prev.has(customerId)) {
          return prev;
        }

        const next = new Set(prev);
        next.delete(customerId);
        return next;
      });
    },
    []
  );

  const updateCustomerDataOnSupabase = useCallback(
    async (
      customer: TapTapCustomerData | undefined,
      columnName: string,
      value: any
    ) => {
      if (isStatusLoading || !customer?.id) {
        return;
      }

      setIsStatusLoading(true);

      const { error } = await taptapUpdateCustomerViaId(customer.id, {
        [columnName]: value
      });

      if (error) {
        snackBar.show({
          severity: 'error',
          message: 'Failed to update TapTap ticket.',
          useSound: true
        });
        setIsStatusLoading(false);
        return;
      }

      if (columnName === taptapCustomerField.STATUS) {
        setPendingFocusStatus(value);

        await createTicketLog({
          ticket_id: customer.id,
          status: value,
          updated_by:
            `${userDetails?.first_name || ''} ${userDetails?.last_name ||
              ''}`.trim() || 'Current User'
        });

        snackBar.show({
          severity: 'success',
          message: `Successfully updated status to ${value}`,
          useSound: true
        });
      }

      const previousStatusColumn = visibleDashboardColumns.find(
        (column) => column.value === customer.status
      );
      const nextStatusColumn = visibleDashboardColumns.find(
        (column) => column.value === value
      );

      dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
      if (previousStatusColumn) {
        fetchCustomersByColumn(previousStatusColumn);
      }
      if (
        nextStatusColumn &&
        nextStatusColumn.key !== previousStatusColumn?.key
      ) {
        fetchCustomersByColumn(nextStatusColumn);
      }
      if (selectedCustomerId === customer.id) {
        await fetchPreviewHistory(customer.id);
      }
      setIsStatusLoading(false);
    },
    [
      createTicketLog,
      dispatch,
      fetchCustomersByColumn,
      fetchPreviewHistory,
      isStatusLoading,
      selectedCustomerId,
      snackBar,
      userDetails,
      visibleDashboardColumns
    ]
  );

  const onChangeStatus = useCallback(
    (value: string) => {
      if (isReadOnly) {
        return;
      }

      if (value === taptapStatuses.HOLD) {
        setPendingStatus(value);
        setIsHoldModalOpen(true);
        return;
      }

      updateCustomerDataOnSupabase(
        selectedCustomer,
        taptapCustomerField.STATUS,
        value
      );
    },
    [isReadOnly, selectedCustomer, updateCustomerDataOnSupabase]
  );

  const onProceedNextStatus = useCallback(
    (status?: string) => {
      const targetStatus = status || nextStatus;

      if (isReadOnly || !selectedCustomer || !targetStatus || !canProceed) {
        return;
      }

      updateCustomerDataOnSupabase(
        selectedCustomer,
        taptapCustomerField.STATUS,
        targetStatus
      );
      closePreviewDialog();
    },
    [
      canProceed,
      isReadOnly,
      nextStatus,
      selectedCustomer,
      updateCustomerDataOnSupabase
    ]
  );

  const onSubmitHoldReason = async () => {
    if (!selectedCustomer?.id) {
      return;
    }

    const { data, error } = await taptapGetCustomerDetailsService(
      Number(selectedCustomer.id)
    );

    if (error || !data || data?.length === 0) {
      console.error('No data found', error);
      return;
    }

    const response: TapTapCustomerData = data[0];
    const updatedReason = response.remarks
      ? `${dateToday()} - Hold Reason: ${holdReason}\n${response.remarks}`
      : `${dateToday()} - Hold Reason: ${holdReason}`;

    setIsHoldModalOpen(false);
    await updateCustomerDataOnSupabase(
      selectedCustomer,
      taptapCustomerField.STATUS,
      pendingStatus
    );
    await updateCustomerDataOnSupabase(
      selectedCustomer,
      taptapCustomerField.REMARKS,
      updatedReason
    );
    setHoldReason('');
    setPendingStatus(null);
  };

  return (
    <Box
      className={classes.root}
      style={{ opacity: isCustomersLoading ? 0.3 : 1 }}
    >
      <LoaderBar isLoading={isCustomersLoading} />
      {/* Overview */}
      {columnsWithCustomers.length > 0 ? (
        <Box className={classes.board}>
          {columnsWithCustomers.map((column) => (
            <div
              key={column.key}
              ref={(element: HTMLDivElement | null) => {
                columnRefs.current[column.key] = element;
              }}
              className={`${classes.column} ${
                focusedColumnKey === column.key ? classes.focusedColumn : ''
              }`}
            >
              <Box className={classes.columnHeader}>
                <Typography
                  variant="h5"
                  style={{ color: column.bgColor || colors.grey[800] }}
                >
                  {column.label}
                </Typography>
                <Chip
                  size="small"
                  label={
                    typeof column.count === 'number'
                      ? `${column.customers.length} / ${column.count}`
                      : column.customers.length
                  }
                />
              </Box>
              <Divider />
              <Box
                className={classes.cards}
                mt={1.5}
                onScroll={(event) => handleColumnScroll(event, column)}
                sx={{}}
              >
                {column.customers.length > 0 ? (
                  column.customers.map((customer) => (
                    <Card
                      key={`${column.key}-${customer.id}`}
                      elevation={2}
                      className={classes.card}
                      onClick={() => openPreviewDialog(customer.id)}
                    >
                      {customer.id && newCommentCustomerIds.has(customer.id) ? (
                        <Box className={classes.commentEmblem}>
                          <CommentIcon style={{ fontSize: 14 }} />
                        </Box>
                      ) : null}

                      <CardContent className={classes.cardContent}>
                        <Box className={classes.titleRow}>
                          <Typography variant="h6">
                            {`${customer.first_name ||
                              ''} ${customer.last_name || ''}`.trim() || 'N/A'}
                          </Typography>
                          {customer.internal_transaction_no ? (
                            <Typography variant="body2" color="textSecondary">
                              {customer.internal_transaction_no}
                            </Typography>
                          ) : null}
                        </Box>

                        <Box className={classes.metaRow}>
                          {customer.tag ? (
                            <Chip
                              icon={
                                <LocalOfferIcon
                                  style={{ color: colors.common.white }}
                                />
                              }
                              size="small"
                              label={customer?.tag || '--'}
                              style={{
                                backgroundColor: '#F9A825',
                                color: 'white',
                                fontWeight: 700
                              }}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            label={customer.status || '--'}
                            style={{
                              backgroundColor: getStatusBgColor(
                                customer.status
                              ),
                              color: colors.common.white,
                              fontWeight: 700
                            }}
                          />
                          <Chip
                            size="small"
                            label={`Balance: ${formatCurrency(
                              customer.balance
                            )}`}
                          />
                        </Box>

                        <Typography variant="body2" style={{ marginTop: 8 }}>
                          <span
                            className={classes.branchText}
                            style={{
                              color: getBranchColor(customer.branch_released)
                            }}
                          >
                            {customer.branch_released || '--'}
                          </span>
                          {' - '}
                          {formatDate(
                            customer.created_at,
                            'MMM d, yyyy h:mm a'
                          )}
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                          {[
                            customer.source,
                            customer.courier,
                            customer.service_type,
                            customer.kind_of_sales_transfer
                          ]
                            .filter(Boolean)
                            .join(' | ') || '--'}
                        </Typography>
                        {customer.status !== taptapStatuses.COMPLETED ? (
                          <TapTapRunningDuration
                            createdAt={customer.created_at}
                            hasLog={customer.running_status_has_log}
                          />
                        ) : null}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Typography className={classes.empty} variant="body2">
                    No tickets
                  </Typography>
                )}
                {column.loading ? (
                  <Box className={classes.loader}>
                    <CircularProgress size={20} />
                  </Box>
                ) : null}
              </Box>
            </div>
          ))}
        </Box>
      ) : (
        <Typography className={classes.empty} variant="body2">
          No tickets
        </Typography>
      )}
      <LoaderBar isLoading={isNextPageLoading} />

      {/* TICKET DETAILS DIALOG */}
      <TicketDialog
        open={Boolean(selectedCustomer)}
        selectedCustomer={selectedCustomer}
        timeline={timeline}
        isReadOnly={isReadOnly}
        isStatusLoading={isStatusLoading}
        isHistoryLoading={isHistoryLoading}
        classes={classes}
        paymentTotal={paymentTotal}
        onClose={closePreviewDialog}
        onChangeStatus={onChangeStatus}
        onEditPayment={onEditPayment}
        nextAction={nextAction}
        onHasSystemGeneratedTicketChange={
          setHasSelectedCustomerSystemGeneratedTicket
        }
        onCommentFetchingChange={setIsCommentLoading}
        onProceedNextStatus={onProceedNextStatus}
      />

      <HoldModal
        title="Reason for changing the transaction status to HOLD"
        placeholder="Hold reason: "
        fieldName="Reason"
        holdReason={holdReason}
        setHoldReason={setHoldReason}
        visible={isHoldModalOpen}
        onHandleClose={() => {
          setIsHoldModalOpen(false);
        }}
        onSubmitHoldReason={onSubmitHoldReason}
      />
    </Box>
  );
};

export const OperationDashboard = memo(Component);
